/* iter-ab1 — Generate Audiobook dialog.
 *
 * Per-production Stripe billing (Feb-2026):
 *   - Users must purchase a production run before generating.
 *   - Admin / God Mode bypass the gate entirely.
 *   - POST /projects/{pid}/audiobook/production-checkout → Stripe.
 *   - On return (?audiobook_production=success), status re-checked.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  Headphones, Loader2, Lock, Download, CheckCircle2, XCircle, Clock, SkipForward, ShoppingCart, Mic,
  Play, Square,
} from 'lucide-react';
import { usePW } from '@/lib/store';
import { useCapabilities } from '@/lib/useCapabilities';
import { audiobookApi } from '@/lib/api';

const STATUS_META = {
  queued:  { icon: Clock,        label: 'Queued',  cls: 'text-muted-foreground' },
  done:    { icon: CheckCircle2, label: 'Ready',   cls: 'text-emerald-600' },
  error:   { icon: XCircle,      label: 'Failed',  cls: 'text-rose-600' },
  skipped: { icon: SkipForward,  label: 'Skipped (no text)', cls: 'text-amber-600' },
  locked:  { icon: Lock,         label: 'Locked',  cls: 'text-muted-foreground/70' },
};

export default function AudiobookDialog({ open, onOpenChange }) {
  const { project } = usePW();
  const { can, upgradeCta } = useCapabilities();
  const canGenerate = can('audiobook_generation');
  const canClone    = can('audiobook_voice_cloning');

  const [voices, setVoices] = useState([]);
  const [fishVoices, setFishVoices] = useState([]);
  const [provider, setProvider] = useState('openai');
  const [voice, setVoice] = useState('');
  const [job, setJob] = useState(undefined); // undefined = loading, null = none
  const [busy, setBusy] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [productionPaid, setProductionPaid] = useState(null); // null = loading
  const [productionPrice, setProductionPrice] = useState(null);
  const pollRef = useRef(null);

  // ── Voice preview state ────────────────────────────────────────────────────
  // previewLoadingId  = voice doc id currently being fetched
  // previewPlayingId  = voice doc id whose audio is actively playing
  // audioRef          = the current HTMLAudioElement (so we can stop it)
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [previewPlayingId, setPreviewPlayingId] = useState(null);
  const previewAudioRef = useRef(null);

  const stopPreview = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setPreviewPlayingId(null);
  }, []);

  const playPreview = useCallback(async (voiceDocId, e) => {
    e.stopPropagation(); // don't select the card
    if (previewPlayingId === voiceDocId) { stopPreview(); return; }
    stopPreview();
    setPreviewLoadingId(voiceDocId);
    try {
      const url = audiobookApi.fishVoicePreviewUrl(voiceDocId);
      const audio = new Audio(url);
      audio.crossOrigin = 'anonymous';
      audio.onended = () => { setPreviewPlayingId(null); previewAudioRef.current = null; };
      audio.onerror = () => {
        toast.error('Preview unavailable — the voice may not have a sample yet.');
        setPreviewPlayingId(null); setPreviewLoadingId(null); previewAudioRef.current = null;
      };
      previewAudioRef.current = audio;
      await audio.play();
      setPreviewPlayingId(voiceDocId);
    } catch (err) {
      toast.error('Could not play preview.');
    } finally {
      setPreviewLoadingId(null);
    }
  }, [previewPlayingId, stopPreview]);

  // Stop preview when dialog closes
  useEffect(() => { if (!open) stopPreview(); }, [open, stopPreview]);

  const loadStatus = useCallback(async () => {
    if (!project?.id) return;
    try {
      const r = await audiobookApi.status(project.id);
      setJob(r.job || null);
    } catch {
      setJob(null);
    }
  }, [project?.id]);

  const loadProductionStatus = useCallback(async () => {
    if (!project?.id) return;
    try {
      const [statusRes, pricesRes] = await Promise.all([
        audiobookApi.productionStatus(project.id),
        audiobookApi.productionPrices(),
      ]);
      setProductionPaid(statusRes.paid);
      setProductionPrice(pricesRes);
    } catch {
      setProductionPaid(false);
      setProductionPrice(null);
    }
  }, [project?.id]);

  useEffect(() => {
    if (!open || !project?.id) return;
    audiobookApi.voices().then((r) => {
      setVoices(r.voices || []);
      setVoice((v) => v || r.default || '');
    }).catch(() => setVoices([]));
    audiobookApi.fishVoices().then((r) => setFishVoices(r.voices || [])).catch(() => setFishVoices([]));
    loadStatus();
    loadProductionStatus();
  }, [open, project?.id, loadStatus, loadProductionStatus]);

  // Poll while a job is actively running.
  useEffect(() => {
    const active = job && (job.status === 'running' || job.status === 'queued');
    if (!open || !active) {
      clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(loadStatus, 4000);
    return () => clearInterval(pollRef.current);
  }, [open, job, loadStatus]);

  const switchProvider = (p) => {
    if (p === 'fish' && !canClone) {
      const cta = upgradeCta('audiobook_voice_cloning');
      toast.info(cta.title, { description: `${cta.body} · Upgrade in Settings → Subscription.`, duration: 4500 });
      return;
    }
    setProvider(p);
    setVoice(p === 'fish' ? (fishVoices[0]?.id || '') : (voices[0]?.id || ''));
  };

  const purchaseProduction = async () => {
    if (!project?.id) return;
    setPurchasing(true);
    try {
      const r = await audiobookApi.productionCheckout(project.id, provider);
      if (r?.already_paid) {
        toast.success('Production already purchased — you can generate now.');
        setProductionPaid(true);
        setPurchasing(false);
        return;
      }
      if (r?.url) {
        window.location.href = r.url;
      } else {
        toast.error('Checkout URL missing from response.');
        setPurchasing(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout.');
      setPurchasing(false);
    }
  };

  const generate = async () => {
    if (!canGenerate) {
      const cta = upgradeCta('audiobook_generation');
      toast.info(cta.title, { description: `${cta.body} · Upgrade in Settings → Subscription.`, duration: 4500 });
      return;
    }
    if (!voice) { toast.error('Pick a narrator voice first.'); return; }
    setBusy(true);
    try {
      const r = await audiobookApi.generate(project.id, voice, provider);
      toast.success(`Narrating ${r.total_chapters} chapter(s)…`);
      await loadStatus();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.code === 'audiobook_production_unpaid') {
        // Server-side gate — shouldn't normally appear since we gate the UI too
        setProductionPaid(false);
        toast.info('Purchase a production run first.');
      } else {
        toast.error(
          typeof detail === 'string' ? detail
            : e?.response?.status === 409 ? 'An audiobook job is already running for this novel.'
            : 'Could not start audiobook generation.'
        );
      }
      await loadStatus();
    } finally {
      setBusy(false);
    }
  };

  const activeVoiceList = provider === 'fish' ? fishVoices : voices;
  const running = job && (job.status === 'running' || job.status === 'queued');
  const pct = job && job.total_chapters ? Math.round((job.completed_chapters / job.total_chapters) * 100) : 0;

  const applicablePrice = productionPrice
    ? (provider === 'fish'
        ? productionPrice.fish_usd
        : productionPrice.is_byok ? productionPrice.byok_usd : productionPrice.standard_usd)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="audiobook-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Headphones className="h-4 w-4 text-primary" /> Generate Audiobook
          </DialogTitle>
          <DialogDescription>
            Narrate {project?.title ? <strong>{project.title}</strong> : 'this novel'} into a full audiobook.
            Choose a voice, purchase a production run, and narration runs in the background.
          </DialogDescription>
        </DialogHeader>

        {/* Provider picker */}
        <div className="flex gap-2" data-testid="audiobook-provider-row">
          <button
            type="button"
            onClick={() => switchProvider('openai')}
            disabled={running}
            data-testid="audiobook-provider-openai"
            className={`flex-1 rounded-md border px-3 py-2 text-left text-[12px] transition-colors disabled:opacity-60 ${
              provider === 'openai' ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card hover:bg-secondary'
            }`}
          >
            <div className="font-semibold">AI Narrator</div>
            <div className="text-[10.5px] text-muted-foreground">OpenAI studio voices · Pro+</div>
          </button>
          <button
            type="button"
            onClick={() => switchProvider('fish')}
            disabled={running}
            data-testid="audiobook-provider-fish"
            className={`flex-1 rounded-md border px-3 py-2 text-left text-[12px] transition-colors disabled:opacity-60 ${
              provider === 'fish' ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card hover:bg-secondary'
            }`}
          >
            <div className="flex items-center gap-1 font-semibold">
              Cloned Voice
              {!canClone && <Lock className="h-3 w-3 text-muted-foreground" />}
            </div>
            <div className="text-[10.5px] text-muted-foreground">Fish Audio · Premier Studio</div>
          </button>
        </div>

        {/* Voice select — Fish voices use visual cards, OpenAI uses a dropdown */}
        <div data-testid="audiobook-voice-row">
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-muted-foreground">Narrator voice</label>

          {provider === 'fish' ? (
            /* ── Fish Audio: card picker ───────────────────────────────────── */
            fishVoices.length === 0 ? (
              <p className="rounded-md border border-dashed border-border p-3 text-[11.5px] text-muted-foreground">
                No cloned voices available yet — ask an admin to add one in the Voice Library.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="fish-voice-card-grid">
                {fishVoices.map((v) => {
                  const active = voice === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => !running && setVoice(v.id)}
                      disabled={running}
                      data-testid={`fish-voice-card-${v.id}`}
                      className={`group relative flex flex-col items-start gap-1 rounded-md border p-2.5 text-left transition-all disabled:opacity-60 ${
                        active
                          ? 'border-primary bg-primary/10 shadow-sm'
                          : 'border-border bg-card hover:border-primary/50 hover:bg-secondary/60'
                      }`}
                    >
                      {/* Active indicator */}
                      {active && (
                        <span className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] text-primary-foreground">✓</span>
                      )}
                      {/* Voice icon */}
                      <span className={`flex h-8 w-8 items-center justify-center rounded-md ${active ? 'bg-primary/20 text-primary' : 'bg-secondary text-muted-foreground'}`}>
                        <Mic className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[12px] font-semibold leading-tight">{v.title}</p>
                        <p className="text-[10px] text-muted-foreground">Cloned voice</p>
                      </div>
                      {/* Preview play / stop button */}
                      <button
                        type="button"
                        onClick={(e) => playPreview(v.id, e)}
                        data-testid={`fish-voice-preview-btn-${v.id}`}
                        title={previewPlayingId === v.id ? 'Stop preview' : 'Play 10-second preview'}
                        className={`mt-1 flex w-full items-center justify-center gap-1 rounded-sm border py-0.5 text-[10px] font-medium transition-colors ${
                          previewPlayingId === v.id
                            ? 'border-rose-300 bg-rose-50 text-rose-600 hover:bg-rose-100 dark:border-rose-700/40 dark:bg-rose-950/30'
                            : 'border-border bg-secondary/50 text-muted-foreground hover:border-primary/50 hover:text-primary'
                        }`}
                      >
                        {previewLoadingId === v.id ? (
                          <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        ) : previewPlayingId === v.id ? (
                          <><Square className="h-2.5 w-2.5" /> Stop</>
                        ) : (
                          <><Play className="h-2.5 w-2.5" /> Preview</>
                        )}
                      </button>
                    </button>
                  );
                })}
              </div>
            )
          ) : (
            /* ── OpenAI / standard: dropdown ──────────────────────────────── */
            activeVoiceList.length === 0 ? (
              <p className="text-[11.5px] text-muted-foreground">Loading voices…</p>
            ) : (
              <Select value={voice} onValueChange={setVoice} disabled={running}>
                <SelectTrigger data-testid="audiobook-voice-select" className="h-9 text-[12.5px]">
                  <SelectValue placeholder="Choose a voice" />
                </SelectTrigger>
                <SelectContent>
                  {activeVoiceList.map((v) => (
                    <SelectItem key={v.id} value={v.id} data-testid={`audiobook-voice-option-${v.id}`}>
                      {v.label || v.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          )}
        </div>

        {/* Production gate — Purchase or Generate */}
        {productionPaid === null ? (
          <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground" data-testid="audiobook-production-loading">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking production status…
          </div>
        ) : productionPaid ? (
          <button
            type="button"
            onClick={generate}
            disabled={busy || running || !voice}
            data-testid="audiobook-generate-btn"
            className="inline-flex items-center justify-center gap-1.5 rounded-sm bg-primary px-3 py-2 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Headphones className="h-3.5 w-3.5" />}
            {running ? 'Narrating…' : 'Generate Audiobook'}
          </button>
        ) : (
          <div className="space-y-2" data-testid="audiobook-purchase-gate">
            <p className="text-[11.5px] text-muted-foreground">
              Full-novel narration requires a one-time production purchase per novel.
              {applicablePrice && (
                <span className="ml-1 font-semibold text-foreground">
                  {provider === 'fish' ? 'Fish Audio price: '
                    : productionPrice?.is_byok ? 'BYOK platform fee: ' : 'Price: '}
                  ${Number(applicablePrice).toFixed(2)}
                </span>
              )}
            </p>
            <button
              type="button"
              onClick={purchaseProduction}
              disabled={purchasing || running}
              data-testid="audiobook-purchase-btn"
              className="inline-flex items-center justify-center gap-1.5 rounded-sm bg-primary px-4 py-2 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {purchasing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShoppingCart className="h-3.5 w-3.5" />}
              {purchasing ? 'Opening checkout…' : `Purchase Production${applicablePrice ? ` — $${Number(applicablePrice).toFixed(2)}` : ''}`}
            </button>
          </div>
        )}

        {/* Job progress + chapter list */}
        {job === undefined ? null : job === null ? (
          <p className="text-[11.5px] text-muted-foreground" data-testid="audiobook-no-job">
            No audiobook generated yet for this novel.
          </p>
        ) : (
          <section className="rounded-md border border-border bg-card p-3" data-testid="audiobook-job-panel">
            <div className="mb-2 flex items-center justify-between text-[11.5px]">
              <span className="font-semibold">
                {job.status === 'done' ? 'Audiobook ready' : job.status === 'error' ? 'Generation failed' : 'Narrating…'}
              </span>
              <span className="text-muted-foreground">{job.completed_chapters}/{job.total_chapters} chapters</span>
            </div>
            <Progress value={pct} className="mb-3 h-1.5" />
            {job.error && <p className="mb-2 text-[11px] text-rose-600" data-testid="audiobook-job-error">{job.error}</p>}
            <div className="max-h-56 space-y-1 overflow-y-auto" data-testid="audiobook-chapter-list">
              {job.chapters.map((c) => {
                const meta = STATUS_META[c.status] || STATUS_META.queued;
                const Icon = meta.icon;
                return (
                  <div
                    key={c.number}
                    className="flex items-center justify-between gap-2 rounded-sm px-2 py-1 text-[11.5px] hover:bg-secondary/60"
                    data-testid={`audiobook-chapter-${c.number}`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Icon className={`h-3.5 w-3.5 ${meta.cls}`} />
                      Chapter {c.number}
                      <span className={`text-[10px] ${meta.cls}`}>{meta.label}</span>
                    </span>
                    {c.status === 'done' && c.audio_url && (
                      <span className="flex items-center gap-2">
                        <audio controls preload="none" className="h-6 w-40" src={audiobookApi.chapterAudioUrl(job.id, c.number)} data-testid={`audiobook-chapter-player-${c.number}`} />
                        <a
                          href={audiobookApi.chapterAudioUrl(job.id, c.number)}
                          download={`chapter-${c.number}.mp3`}
                          title="Download"
                          data-testid={`audiobook-chapter-download-${c.number}`}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </a>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
}

